import Grid from '@mui/material/Grid';
import { RHFAutoComplete } from '@openx/components-core/lib/AutoComplete/RHFAutoComplete.tsx';
import { useCallback, useEffect, useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useDealContext } from '../../../utils/context/DealContext.tsx';
import { FiltersDropdown } from '../components/FiltersDropdown.tsx';
import { SectionTitle } from '../components/SectionTitle.tsx';
import { SelectedFilters } from '../components/SelectedFilters.tsx';
import {
  Filters,
  getFilterOptions,
  SupplementalAdQualityField,
  type SupplementalAdQualityFormState,
} from '../constants.ts';

export const CreativeTypesSection = () => {
  const { useCreativeTypesFetch } = useDealContext();
  const { setValue, control } = useFormContext<SupplementalAdQualityFormState>();

  const observableCreativeTypesState = useWatch({ control, name: 'creative_types' });
  const creativeTypesOp = observableCreativeTypesState?.op ?? 'current';
  const creativeTypesIds = observableCreativeTypesState?.ids ?? [];

  const { t } = useTranslation();
  const filterOptions = useMemo(() => getFilterOptions(t), [t]);
  const { creativeTypes, isCreativeTypesLoading, refetchCreativeTypes } = useCreativeTypesFetch();

  useEffect(() => {
    if (creativeTypesOp === Filters.ALLOW) {
      refetchCreativeTypes();
    }
  }, [refetchCreativeTypes, creativeTypesOp]);

  const handleFilterTypeChange = useCallback(
    (_event, value) => {
      setValue(SupplementalAdQualityField.CREATIVE_TYPES_IDS, []);
      setValue(SupplementalAdQualityField.CREATIVE_TYPES_OP, value);
    },
    [setValue]
  );

  const handleChange = useCallback(
    (value: string[]) => {
      setValue(SupplementalAdQualityField.CREATIVE_TYPES_IDS, value);
    },
    [setValue]
  );

  return (
    <>
      <SectionTitle title={t('Creative Types')} />
      <Grid container spacing={1}>
        <Grid size={6}>
          <RHFAutoComplete
            name={SupplementalAdQualityField.CREATIVE_TYPES_OP}
            textFieldProps={{ margin: 'dense' }}
            disableClearable={true}
            getOptionLabel={option => filterOptions[option]?.name || option}
            options={Object.keys(filterOptions)}
            onChange={handleFilterTypeChange}
            fullWidth
            data-test="saq-creative"
          />
        </Grid>
        {creativeTypesOp === Filters.ALLOW && (
          <Grid size={6}>
            <FiltersDropdown
              options={creativeTypes}
              loading={isCreativeTypesLoading}
              selectedOptions={creativeTypesIds}
              placeholder={t('[ Search A Creative Types ]')}
              onChange={handleChange}
              dataTest="creative-search"
            />
          </Grid>
        )}
      </Grid>
      {creativeTypesIds.length > 0 && (
        <SelectedFilters
          selectedOptions={creativeTypesIds}
          handleChange={handleChange}
          options={creativeTypes}
          title={`${t('Creative Types')} [ ${creativeTypesIds.length} ]`}
          filtersTitle={filterOptions.allow.name}
        />
      )}
    </>
  );
};
